import { useState, useRef, useEffect } from 'react';

const cards = [
  {
    title: '「考え続けてしまう」のは、心の防衛反応',
    summary: '正解を探し続けるのは、これ以上傷つきたくないという心の自然な仕組み。あなたが真剣に生きてきた証拠です。',
    example: 'たとえば——「離婚すべきか、このまま続けるべきか」をずっと考え続けてしまう。どちらを選んでも後悔しそうで、思考が止まらない。',
    detail:
      'この「考え続ける」は、実は過去に「間違った選択をして傷ついた」経験から心が学んだ防衛パターンです。考えている間は「まだ間違っていない」と感じられるから、思考を止められないのです。これは弱さではなく、あなたの心の賢さです。',
  },
  {
    title: '「自分の感覚がわからない」には、背景がある',
    summary: '本音より誰かの期待を優先してきた経験が長いと、自分の「どうしたい」が見えにくくなります。これは性格ではなく、環境への適応です。',
    example: 'たとえば——「自分の中に答えがある」と言われても、それがどれなのか分からない。自分の感覚を信じたいのに、過去の失敗の方に目がいってしまう。',
    detail:
      '子どもの頃から「あなたはどうしたい？」ではなく「こうしなさい」と言われて育つと、自分の感覚をキャッチするセンサーが弱くなります。これは壊れたのではなく、眠っているだけ。安全な場所で少しずつ起こしていくことができます。',
  },
  {
    title: '「変わることへの抵抗」にも、ちゃんと理由がある',
    summary: '「変わりたい」と「変わるのが怖い」が同時にあるのは矛盾ではありません。今までの自分を守りたいという、とても自然な気持ちです。',
    example: 'たとえば——セッションに来てくれたけど「ここまでの努力が無駄になる気がする」「自分を否定された気がする」と感じて、変化の手前で止まってしまう方がいます。',
    detail:
      '変わることへの抵抗は、今までの自分を「間違いだった」と認めることへの恐れから来ています。でも、sanctuaryでは「変える」ことを目標にしません。今のあなたのまま、何が起きているのかを一緒に見るだけ。変化は結果として起きるもので、目標にするものではないのです。',
  },
];

function AccordionCard({ card, index }: { card: (typeof cards)[0]; index: number }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const num = String(index + 1).padStart(2, '0');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="opacity-0 translate-y-8 transition-all duration-700 bg-white rounded-lg p-8"
      style={{ transitionDelay: `${index * 150}ms`, border: '1px solid #EDE7DC' }}
    >
      <div className="flex items-start gap-5 mb-4">
        {/* Number label */}
        <div className="flex-shrink-0 flex flex-col items-center pt-1">
          <span
            className="font-serif italic leading-none tracking-tight"
            style={{ fontSize: '2rem', color: 'rgba(143, 166, 139, 0.35)', lineHeight: 1 }}
          >
            {num}
          </span>
          <div className="w-px h-4 mt-1" style={{ backgroundColor: 'rgba(143, 166, 139, 0.3)' }} />
        </div>
        <h3 className="font-serif text-[#3A3530] text-lg leading-snug">{card.title}</h3>
      </div>

      <p className="font-sans text-[#6B6460] text-sm leading-relaxed mb-4 pl-14">{card.summary}</p>

      {/* Example box */}
      <div
        className="pl-14 mb-5"
      >
        <div
          className="rounded-lg px-5 py-4"
          style={{ border: '1px solid #F5C9A0', backgroundColor: '#FFF8F2' }}
        >
          <p className="font-sans text-[#7A5C45] text-sm leading-relaxed">{card.example}</p>
        </div>
      </div>

      <div className="pl-14">
        <button
          onClick={() => setOpen(!open)}
          className="whitespace-nowrap cursor-pointer flex items-center gap-2 font-sans text-xs tracking-wider text-[#8FA68B] hover:text-[#6B8A67] transition-colors duration-200"
        >
          <span>{open ? '閉じる' : 'もう少し詳しく'}</span>
          <span
            className="inline-block transition-transform duration-300"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            ↓
          </span>
        </button>
        <div
          className="overflow-hidden transition-all duration-500"
          style={{ maxHeight: open ? '400px' : '0', opacity: open ? 1 : 0 }}
        >
          <p className="font-sans text-[#6B6460] text-sm leading-loose mt-4 pb-2">
            {card.detail}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function WhySection() {
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Nature divider image */}
      <div className="w-full h-48 md:h-64 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=calm%20ocean%20sea%20at%20dusk%20sunset%20soft%20pink%20lavender%20purple%20light%20blue%20reflections%20on%20water%20surface%20no%20people%20no%20buildings%20no%20artificial%20objects%20nature%20only%20peaceful%20serene%20minimal%20photography%20dreamy%20pastel%20tones%20gentle%20waves%20horizon&width=1440&height=400&seq=sanctuary-ocean-divider-1&orientation=landscape"
          alt="海の風景"
          className="w-full h-full object-cover object-center"
        />
      </div>

      <section className="w-full bg-[#FAF8F5] py-24 px-4" id="why-section">
        <div className="max-w-2xl mx-auto">
          <div ref={titleRef} className="opacity-0 translate-y-8 transition-all duration-1000 mb-16 text-center">
            <p className="font-sans text-[#8FA68B] text-xs tracking-[0.4em] mb-6 uppercase">why</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#3A3530] leading-snug">
              学んでも変わらなかったのは、<br />
              あなたの努力が足りなかったからじゃない。
            </h2>
            <p className="font-sans text-[#8FA68B] text-sm leading-relaxed mt-5 tracking-wide">
              心の仕組みから見ると、そこにはちゃんと理由があります。
            </p>
          </div>

          <div className="space-y-5">
            {cards.map((card, i) => (
              <AccordionCard key={i} card={card} index={i} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
